import { WORKLOAD_STATES, type WorkloadStateName, type WorkloadSummary } from '@pulse/contracts';
import * as React from 'react';

import { SourceLink } from '@/components/pulse/source-link';

/** Each state's colour, as the prototype's WL_STATES paints them. */
const STATE_COLOUR: Readonly<Record<WorkloadStateName, string>> = {
  Overdue: 'var(--s-block)',
  'In progress': 'var(--s-prog)',
  'Ready or due': 'var(--s-ready)',
  'In review': 'var(--s-review)',
  'Blocked or waiting': 'var(--s-ready)',
  'Backlog, no date': 'var(--s-draft)',
};

const present = (summary: WorkloadSummary) => WORKLOAD_STATES.filter((state) => (summary.counts[state] ?? 0) > 0);

/**
 * The prototype's `wlBar()`: a bar stacked by workload state. With `scale`, the bar's length is the
 * member's total against the largest, so every bar on a page shares one scale (PPL-2).
 */
export function WorkloadBar({ summary, scale }: { readonly summary: WorkloadSummary; readonly scale?: number }): React.ReactElement {
  const states = present(summary);
  const label = states.map((state) => `${summary.counts[state]} ${state.toLowerCase()}`).join(', ') || 'Nothing assigned';
  const width = scale === undefined || scale === 0 ? '100%' : `${Math.max((summary.total / scale) * 100, 6).toFixed(1)}%`;
  return (
    <span className="wl-bar" role="img" aria-label={label} style={{ width }}>
      {states.map((state) => (
        <i
          key={state}
          style={{ width: `${(((summary.counts[state] ?? 0) / summary.total) * 100).toFixed(2)}%`, background: STATE_COLOUR[state] }}
          title={`${summary.counts[state]} ${state.toLowerCase()}`}
        />
      ))}
    </span>
  );
}

/** The prototype's `wlLegend()`: every state and its colour. */
export function WorkloadLegend(): React.ReactElement {
  return (
    <div className="legend">
      {WORKLOAD_STATES.map((state) => (
        <span key={state}>
          <i className="sw" style={{ background: STATE_COLOUR[state] }} />
          {state}
        </span>
      ))}
    </div>
  );
}

/** The prototype's `wlChips()`: one chip per state present, with its count. */
export function WorkloadChips({ summary }: { readonly summary: WorkloadSummary }): React.ReactElement {
  return (
    <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
      {present(summary).map((state) => (
        <span key={state} className="chip c-kind">
          <span className="sw" style={{ background: STATE_COLOUR[state] }} />
          {summary.counts[state]} {state.toLowerCase()}
        </span>
      ))}
    </div>
  );
}

export interface NextItem {
  readonly title: string;
  readonly where: string;
  readonly url: string;
  readonly due: { readonly text: string; readonly tone: 'late' | 'soon' | 'none' };
  readonly tag: string | null;
}

/** The prototype's `nextList()`: each item links to its source, with where it lives and its due label. */
export function NextList({ items }: { readonly items: ReadonlyArray<NextItem> }): React.ReactElement {
  return (
    <div className="stack" style={{ gap: 8 }}>
      {items.map((item) => (
        <SourceLink key={`${item.url}${item.title}`} className="next" href={item.url}>
          <span style={{ fontWeight: 500 }}>{item.title}</span>
          {item.tag !== null ? (
            <span className="chip c-kind" style={{ alignSelf: 'start' }}>
              {item.tag}
            </span>
          ) : (
            <span />
          )}
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{item.where}</span>
          <span className={`due ${item.due.tone}`} style={{ textAlign: 'right' }}>
            {item.due.text}
          </span>
        </SourceLink>
      ))}
    </div>
  );
}
